import {
  createContext,
  useContext,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { type MediaEnrichment } from './UserLibraryContext';
import {
  useMyWatchlists,
  useToggleListItemMutation,
  useSetListItemDesiredMutation,
  useCreateWatchlistMutation,
  useOptimisticListUpdates,
  isListItemsQuery,
  getAnyListItemsData,
  setAllListItemsData,
  invalidateAllListItemsQueries,
} from '../hooks/useListsQuery';
import { queryKeys } from '../lib/queryKeys';
import { enqueueIntent } from '../lib/outbox';

// Phase 4C: Most constants removed - React Query handles caching/staleness
const PAGE_LIMIT = 100;

export type MediaType = 'movie' | 'tv';
export type MediaFilter = 'movie' | 'tv' | 'mixed';
export type CollaboratorRole = 'editor' | 'viewer';

export interface WatchlistMeta {
  id: string;
  name: string;
  slug: string | null;
  description?: string | null;
  visibility: 'public' | 'friends' | 'private';
  list_type?: 'watchlist' | 'watched' | 'favorites' | 'custom';
  is_default: boolean;
  cover_url?: string | null;
  item_count: number;
  follower_count?: number;
  is_official?: boolean;
  media_filter: MediaFilter;
  created_at?: string;
  user_id?: string; // owner (present for collaborator lists in /watchlists response)
  collaborator_role?: CollaboratorRole | null; // present for collaborator lists
  has_collaborators?: boolean; // PART A: "shared out" - owned list has >=1 collaborator
  revision?: number;
  updated_at?: string;
}

export interface WatchlistItemRow {
  id: string;
  watchlist_id: string;
  media_id: number;
  media_type: MediaType;
  media_title: string;
  media_poster?: string | null;
  media_year?: number | null;
  notes?: string | null;
  position: number | null;
  rank?: string | null;
  added_at: string;
  // Optional enrichment fields from server-side Meilisearch lookup
  genre_ids?: number[];
  runtime?: number | null;
  imdb_rating?: number;
  rt_rating?: number;
  filmpl_rating?: number;
  popularity?: number;
  original_language?: string;
  vote_count?: number;
}



export interface ListsContextValue {
  isHydrated: boolean;
  lists: WatchlistMeta[];
  myLists: WatchlistMeta[];
  collabLists: WatchlistMeta[];
  followedLists: Array<{
    id: string;
    name: string;
    description: string | null;
    item_count: number;
    follower_count: number;
    media_filter: MediaFilter;
    followed_at: string;
    owner: { id: string; username: string; avatar_url: string | null };
  }>;
  getListMeta: (listId: string) => WatchlistMeta | null;
  getListItemsStatus: (listId: string) => 'idle' | 'loading' | 'ready' | 'error';
  getListItemsFetchedAtMs: (listId: string) => number | null;
  getListItems: (listId: string, opts?: { orderBy?: 'position' | 'added_at'; order?: 'asc' | 'desc' }) => WatchlistItemRow[];
  getListHasMore: (listId: string) => boolean;
  refresh: () => Promise<void>;
  fetchListItems: (listId: string, opts?: { reset?: boolean }) => Promise<void>;
  ensureList: (
    listId: string,
    opts: { auth: 'optional' | 'required'; reason: 'navigation' | 'focus' | 'interval' | 'manual'; force?: boolean; reset?: boolean }
  ) => Promise<void>;
  loadMoreList: (listId: string, opts?: { auth?: 'optional' | 'required' }) => Promise<void>;
  // WatchlistPage-only: optimistic reorder must update store (canonical) without extra fetch
  optimisticReorderByPosition: (listId: string, orderedKeys: Array<`${MediaType}:${number}`>) => void;
  // WatchlistPage-only: seed store from an already-fetched GET /watchlists/:id response (no network; preserves one-request nav)
  seedFromWatchlistDetail: (
    listId: string,
    detail: { watchlist: any; items: any[]; pagination?: any },
    opts?: { reset?: boolean }
  ) => void;
  isInList: (listId: string, mediaId: number, mediaType: MediaType) => boolean;
  isItemPending: (listId: string, mediaId: number, mediaType: MediaType) => boolean;
  // TASK 3: Added enrichment param for instant metadata display on WatchlistPage
  toggleListItem: (listId: string, mediaId: number, mediaType: MediaType, mediaTitle: string, mediaPoster?: string, mediaYear?: number, knownItemId?: string, forceRemove?: boolean, enrichment?: import('./UserLibraryContext').MediaEnrichment) => Promise<string | null>;
  /**
   * SPAM-SAFE: Set explicit desired membership state (no toggle semantics).
   * Uses per-item AbortController for last-intent-wins behavior.
   * Returns { didChange, direction } for toast correctness.
   */
  setListItemDesired: (
    listId: string,
    mediaId: number,
    mediaType: MediaType,
    mediaTitle: string,
    desiredInList: boolean,
    mediaPoster?: string,
    mediaYear?: number,
    enrichment?: import('./UserLibraryContext').MediaEnrichment
  ) => Promise<{ didChange: boolean; direction?: 'added' | 'removed' }>;
  createWatchlist: (name: string, description?: string, visibility?: 'public' | 'friends' | 'private', mediaFilter?: MediaFilter) => Promise<WatchlistMeta | null>;
}

const ListsContext = createContext<ListsContextValue | null>(null);

export function ListsProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth();
  const queryClient = useQueryClient();

  // Phase 4A: React Query is the source of truth for list reads
  const {
    lists: rqLists,
    myLists: rqMyLists,
    collabLists: rqCollabLists,
    isHydrated: rqIsHydrated,
    refetch: rqRefetch,
  } = useMyWatchlists();

  // Phase 4B: React Query mutations
  const toggleListItemMutation = useToggleListItemMutation();
  const setListItemDesiredMutation = useSetListItemDesiredMutation();
  const createWatchlistMutation = useCreateWatchlistMutation();
  const { addItem: rqAddItem, removeItem: rqRemoveItem } = useOptimisticListUpdates();

  const subscriptionsRef = useRef(new Map<string, ReturnType<typeof supabase.channel>>());
  const tokenRef = useRef<string | null>(null);
  const hasToken = !!session?.access_token;

  useEffect(() => {
    tokenRef.current = session?.access_token ?? null;
  }, [session?.access_token]);

  // Phase 4A: refresh now uses React Query's refetch
  const refresh = useCallback(async () => {
    if (!user?.id) return;
    await rqRefetch();
    // Also invalidate followed lists (handled by separate hook)
    await queryClient.invalidateQueries({ queryKey: queryKeys.watchlist.followed(user.id) });
  }, [user?.id, rqRefetch, queryClient]);

  useEffect(() => {
    if (!user?.id) {
      // Unsubscribe all realtime channels on logout/user switch
      for (const ch of subscriptionsRef.current.values()) ch.unsubscribe();
      subscriptionsRef.current.clear();
      return;
    }
    // Note: React Query handles initial fetch via useMyWatchlists
  }, [user?.id, hasToken]);

  // Phase 4C: fetchListItems now invalidates React Query cache (no ListsStore)
  const fetchListItems = useCallback(
    async (listId: string, _opts?: { reset?: boolean }) => {
      // Simply invalidate React Query cache - it will refetch automatically
      await queryClient.invalidateQueries({ queryKey: queryKeys.lists.detail(listId) });
      invalidateAllListItemsQueries(queryClient, listId);
    },
    [queryClient]
  );

  // Phase 4A: getListMeta checks React Query caches only
  const getListMeta = useCallback((listId: string) => {
    // Check React Query lists cache first
    const rqList = rqLists.find((l) => l.id === listId);
    if (rqList) return rqList;
    // Check React Query detail cache
    const detailData = queryClient.getQueryData<{ watchlist: WatchlistMeta }>(
      queryKeys.lists.detail(listId)
    );
    if (detailData?.watchlist) return detailData.watchlist;
    return null;
  }, [rqLists, queryClient]);

  // Phase 4C: Read functions now proxy to React Query cache (no ListsStore)
  // Use predicate to check ALL sort variants of a list's items query
  const getListItemsStatus = useCallback((listId: string): 'idle' | 'loading' | 'ready' | 'error' => {
    // Find first matching query state for any sort variant
    const queries = queryClient.getQueryCache().findAll({
      predicate: (query) => isListItemsQuery(query.queryKey, listId),
    });
    if (queries.length === 0) return 'idle';
    // Check if any is loading
    if (queries.some(q => q.state.status === 'pending')) return 'loading';
    // Check if any has error
    if (queries.some(q => q.state.status === 'error')) return 'error';
    // If we have any data, it's ready
    if (queries.some(q => q.state.data)) return 'ready';
    return 'idle';
  }, [queryClient]);

  const getListItemsFetchedAtMs = useCallback((listId: string): number | null => {
    // Find first matching query with data for any sort variant
    const queries = queryClient.getQueryCache().findAll({
      predicate: (query) => isListItemsQuery(query.queryKey, listId),
    });
    for (const q of queries) {
      if (q.state.dataUpdatedAt) return q.state.dataUpdatedAt;
    }
    return null;
  }, [queryClient]);

  const getListHasMore = useCallback((listId: string): boolean => {
    // Use helper to get data from any sort variant
    const data = getAnyListItemsData(queryClient, listId);
    if (!data?.pages?.length) return false;
    const lastPage = data.pages[data.pages.length - 1];
    return lastPage?.pagination?.has_more ?? false;
  }, [queryClient]);

  const getListItems = useCallback(
    (listId: string, opts?: { orderBy?: 'position' | 'added_at'; order?: 'asc' | 'desc' }): WatchlistItemRow[] => {
      const orderBy = opts?.orderBy ?? 'position';
      const order = opts?.order ?? 'asc';

      // Read from React Query cache (any sort variant)
      const data = getAnyListItemsData(queryClient, listId);

      if (!data?.pages) return [];

      // Flatten all pages
      const items = data.pages.flatMap((page) => page.items);

      // Sort as requested (RQ stores in position order, so we may need to re-sort)
      const sorted = [...items].sort((a, b) => {
        if (orderBy === 'position') {
          const posA = a.position ?? Number.MAX_SAFE_INTEGER;
          const posB = b.position ?? Number.MAX_SAFE_INTEGER;
          return order === 'asc' ? posA - posB : posB - posA;
        } else {
          const timeA = new Date(a.added_at).getTime();
          const timeB = new Date(b.added_at).getTime();
          return order === 'asc' ? timeA - timeB : timeB - timeA;
        }
      });

      return sorted;
    },
    [queryClient]
  );

  // Phase 4C: ensureList now just ensures React Query has the data (no ListsStore)
  const ensureList = useCallback(
    async (
      listId: string,
      opts: { auth: 'optional' | 'required'; reason: 'navigation' | 'focus' | 'interval' | 'manual'; force?: boolean; reset?: boolean }
    ) => {
      const token = tokenRef.current;
      if (!token && opts.auth === 'required') return;

      // Check if data exists in React Query cache (any sort variant)
      const existingData = getAnyListItemsData(queryClient, listId);

      // Invalidate/refetch based on conditions
      if (opts.force || opts.reset || !existingData) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.lists.detail(listId) });
        invalidateAllListItemsQueries(queryClient, listId);
      }
    },
    [queryClient]
  );

  // Phase 4C: loadMoreList is no longer needed - consumers should use useWatchlistItemsInfinite().fetchNextPage
  // Kept for API compatibility but does nothing (RQ handles pagination)
  const loadMoreList = useCallback(
    async (_listId: string, _opts?: { auth?: 'optional' | 'required' }) => {
      // No-op: consumers should use useWatchlistItemsInfinite().fetchNextPage directly
      console.warn('loadMoreList is deprecated. Use useWatchlistItemsInfinite().fetchNextPage instead.');
    },
    []
  );

  // Phase 4C: seedFromWatchlistDetail now seeds React Query cache (no ListsStore)
  const seedFromWatchlistDetail = useCallback(
    (listId: string, detail: { watchlist: any; items: any[]; pagination?: any }, _opts?: { reset?: boolean }) => {
      const w = detail.watchlist;
      if (!w) return;
      const items = Array.isArray(detail.items) ? detail.items : [];
      const pagination = detail.pagination || {};

      const watchlist: WatchlistMeta = {
        id: w.id,
        name: w.name,
        slug: w.slug ?? null,
        description: w.description ?? null,
        visibility: w.visibility,
        list_type: w.list_type,
        is_default: !!w.is_default,
        cover_url: w.cover_url ?? undefined,
        item_count: w.item_count ?? 0,
        follower_count: w.follower_count ?? undefined,
        is_official: w.is_official ?? undefined,
        media_filter: w.media_filter ?? 'mixed',
        created_at: w.created_at ?? undefined,
        user_id: w.user_id ?? undefined,
        revision: w.revision ?? undefined,
        updated_at: w.updated_at ?? undefined,
        collaborator_role: w.collaborator_role ?? null,
      };

      const rows: WatchlistItemRow[] = items.map((r: any) => ({
        id: r.id,
        watchlist_id: listId,
        media_id: r.media_id,
        media_type: r.media_type,
        media_title: r.media_title,
        media_poster: r.media_poster,
        media_year: r.media_year,
        notes: r.notes,
        position: r.position,
        added_at: r.added_at,
        genre_ids: r.genre_ids,
        runtime: r.runtime,
        imdb_rating: r.imdb_rating,
        rt_rating: r.rt_rating,
        filmpl_rating: r.filmpl_rating,
        popularity: r.popularity,
        original_language: r.original_language,
        vote_count: r.vote_count,
      }));

      const page = typeof pagination.page === 'number' ? pagination.page : 1;
      const limit = typeof pagination.limit === 'number' ? pagination.limit : PAGE_LIMIT;
      const total = typeof pagination.total === 'number' ? pagination.total : null;
      const hasMore = pagination.has_more ?? (items.length === limit);

      // Seed React Query cache with this data
      queryClient.setQueryData(queryKeys.lists.detail(listId), {
        watchlist,
        items: rows,
        pagination: { page, limit, total, has_more: hasMore },
      });

      // Also seed the infinite query cache (all sort variants)
      setAllListItemsData(queryClient, listId, () => ({
        pages: [{
          items: rows,
          pagination: { page, limit, total, has_more: hasMore },
          watchlist,
        }],
        pageParams: [1],
      }));
    },
    [queryClient]
  );

  // Phase 4C: optimisticReorderByPosition now updates React Query cache (no ListsStore)
  const optimisticReorderByPosition = useCallback((listId: string, orderedKeys: Array<`${MediaType}:${number}`>) => {
    // Parse keys back to mediaId/mediaType and build position map
    const positionMap = new Map<string, number>();
    orderedKeys.forEach((key, idx) => {
      positionMap.set(key, idx + 1);
    });

    // Update React Query cache (all sort variants)
    setAllListItemsData(queryClient, listId, (old) => {
      if (!old || old.pages.length === 0) return old;

      const newPages = old.pages.map((page) => ({
        ...page,
        items: page.items.map((item) => {
          const key = `${item.media_type}:${item.media_id}` as `${MediaType}:${number}`;
          const newPosition = positionMap.get(key);
          return newPosition !== undefined ? { ...item, position: newPosition } : item;
        }),
      }));

      return { ...old, pages: newPages };
    });
  }, [queryClient]);


  // Phase 4C: isInList now reads from React Query cache (no ListsStore)
  const isInList = useCallback((listId: string, mediaId: number, mediaType: MediaType) => {
    // Read from any sort variant
    const data = getAnyListItemsData(queryClient, listId);

    if (!data?.pages) return false;

    return data.pages.some((page) =>
      page.items.some((item) => item.media_id === mediaId && item.media_type === mediaType)
    );
  }, [queryClient]);

  // Check if an item has a pending (temp) ID - means optimistic add not yet confirmed by server
  // Used by UI to disable remove action until server confirms the add
  const isItemPending = useCallback((listId: string, mediaId: number, mediaType: MediaType) => {
    // Read from any sort variant
    const data = getAnyListItemsData(queryClient, listId);

    if (!data?.pages) return false;

    for (const page of data.pages) {
      const item = page.items.find((i) => i.media_id === mediaId && i.media_type === mediaType);
      if (item) {
        return item.id.startsWith('temp-');
      }
    }
    return false;
  }, [queryClient]);

  // Phase 4C: toggleListItem uses React Query ONLY (no ListsStore)
  // IMPORTANT: Re-throws errors so callers can show appropriate error toasts
  // Returns: real server item ID on add, existing item ID on remove
  //
  // knownItemId: Optional item_id from membership lookup (for removal when cache is empty)
  // forceRemove: If true, forces removal even if cache shows item not present (trust lookup)
  // TASK 3: enrichment: Optional enrichment fields for instant metadata display on WatchlistPage
  const toggleListItem = useCallback(
    async (
      listId: string,
      mediaId: number,
      mediaType: MediaType,
      mediaTitle: string,
      mediaPoster?: string,
      mediaYear?: number,
      knownItemId?: string,
      forceRemove?: boolean,
      enrichment?: MediaEnrichment
    ): Promise<string | null> => {
      const token = tokenRef.current;
      if (!token) throw new Error('Not authenticated');

      // CRITICAL: Compute isAdd/confirmedItemId BEFORE calling mutation (PRE-OPTIMISTIC snapshot)
      // This is read from cache BEFORE onMutate runs and modifies it with temp items
      const currentData = getAnyListItemsData(queryClient, listId);
      const existingItem = currentData?.pages
        .flatMap((p) => p.items)
        .find((i) => i.media_id === mediaId && i.media_type === mediaType);

      // Only confirmed server UUIDs count as "in list" - temp IDs are pending adds
      const isPendingAdd = existingItem?.id.startsWith('temp-') ?? false;
      const hasConfirmedItem = !!existingItem && !isPendingAdd;

      // TASK D: If there's a pending add, return null (no-op) instead of throwing
      // This removes the grey pending UX - user can keep clicking, we just ignore
      if (isPendingAdd) {
        return null;
      }

      // Compute toggle direction PRE-OPTIMISTIC
      // Use knownItemId if provided (from membership lookup) and forceRemove is true
      let isAdd: boolean;
      let confirmedItemId: string | undefined;

      if (forceRemove && knownItemId) {
        // Caller knows this is a remove (from membership lookup) - trust it
        isAdd = false;
        confirmedItemId = knownItemId;
      } else if (hasConfirmedItem) {
        // Cache has the item with confirmed ID - remove it
        isAdd = false;
        confirmedItemId = existingItem.id;
      } else {
        // Not in cache - add it
        isAdd = true;
        confirmedItemId = undefined;
      }

      // Use React Query mutation (handles optimistic updates and rollback)
      // Errors are re-thrown so callers can show error toasts - never swallow failures
      // TASK 3: Pass enrichment for instant metadata display on WatchlistPage
      const result = await toggleListItemMutation.mutateAsync({
        listId,
        mediaId,
        mediaType,
        mediaTitle,
        mediaPoster,
        mediaYear,
        isAdd,
        confirmedItemId,
        enrichment,
      });

      // Return real server item ID on add, confirmed item ID on remove
      if (result.item) {
        return result.item.id; // Real server-assigned ID on add
      }
      if (result.removed && confirmedItemId) {
        return confirmedItemId; // The removed item's ID
      }
      return null;
    },
    [toggleListItemMutation, queryClient]
  );

  // Phase 5: SPAM-SAFE setListItemDesired - explicit desired state, per-item abort, last-intent-wins
  // Returns { didChange, direction } for toast correctness (only show toast on real state flips)
  // CRITICAL: Computes PRE-OPTIMISTIC snapshot BEFORE calling mutateAsync
  const setListItemDesired = useCallback(
    async (
      listId: string,
      mediaId: number,
      mediaType: MediaType,
      mediaTitle: string,
      desiredInList: boolean,
      mediaPoster?: string,
      mediaYear?: number,
      enrichment?: MediaEnrichment
    ): Promise<{ didChange: boolean; direction?: 'added' | 'removed' }> => {
      const token = tokenRef.current;
      if (!token) throw new Error('Not authenticated');
      if (!user?.id) throw new Error('Not authenticated');

      // OUTBOX: Persist intent BEFORE mutation (survives refresh/navigation)
      // Custom lists only (isDefaultList: false) - default lists handled in UserLibraryContext
      enqueueIntent(user.id, {
        listId,
        isDefaultList: false,
        mediaId,
        mediaType,
        mediaTitle,
        mediaPoster,
        mediaYear,
        desiredInList,
      });

      // MANDATORY: Compute PRE-OPTIMISTIC snapshot BEFORE mutateAsync
      // mutationFn must NEVER read cache - it runs after onMutate has modified it
      const currentData = getAnyListItemsData(queryClient, listId);
      const existingItem = currentData?.pages
        .flatMap((p) => p.items)
        .find((i) => i.media_id === mediaId && i.media_type === mediaType);

      const wasPendingAdd = existingItem?.id.startsWith('temp-') ?? false;
      const inItemsCache = !!existingItem && !wasPendingAdd;

      // FIX: When items cache is cold, fallback to membership batch cache
      // This prevents remove no-op when UI shows "in list" via membership fallback
      let inMembershipCache = false;
      if (!inItemsCache) {
        const membershipQueries = queryClient.getQueriesData<{ in_list_ids: string[] }>({
          queryKey: ['lists', 'membership', mediaId, mediaType],
          exact: false,
        });
        inMembershipCache = membershipQueries.some(
          ([, data]) => data?.in_list_ids?.includes(listId)
        );
      }

      const wasInList = inItemsCache || inMembershipCache;
      // confirmedItemId only available from items cache; mutationFn handles cold case via lookup
      const confirmedItemId = inItemsCache ? existingItem!.id : undefined;

      try {
        const result = await setListItemDesiredMutation.mutateAsync({
          listId,
          mediaId,
          mediaType,
          mediaTitle,
          mediaPoster,
          mediaYear,
          desiredInList,
          enrichment,
          // PRE-OPTIMISTIC snapshot - mutationFn uses these, never reads cache
          wasInList,
          wasPendingAdd,
          confirmedItemId,
        });

        return { didChange: result.didChange, direction: result.direction };
      } catch (err) {
        // AbortError means superseded by newer request - treat as no-op (not an error)
        if (err instanceof Error && err.name === 'AbortError') {
          return { didChange: false };
        }
        // Real errors should propagate for toast handling
        throw err;
      }
    },
    [setListItemDesiredMutation, queryClient, user?.id]
  );

  // Phase 4C: createWatchlist uses React Query ONLY (no ListsStore)
  const createWatchlist = useCallback(
    async (name: string, description?: string, visibility: 'public' | 'friends' | 'private' = 'private', mediaFilter: MediaFilter = 'mixed') => {
      if (!session?.access_token || !user?.id) {
        throw new Error('Not authenticated');
      }

      try {
        // Use React Query mutation (handles optimistic updates and rollback)
        const serverList = await createWatchlistMutation.mutateAsync({
          name,
          description,
          visibility,
          mediaFilter,
        });

        return serverList;
      } catch (e) {
        // React Query mutation handles rollback automatically
        throw e instanceof Error ? e : new Error('Failed to create list');
      }
    },
    [session?.access_token, user?.id, createWatchlistMutation]
  );

  // Phase 4C: Realtime subscription management - React Query ONLY (no ListsStore)
  // (owner/collab only, non-default lists only)
  useEffect(() => {
    if (!session?.access_token || !user?.id) return;

    // Use React Query lists as source of truth for eligible lists
    const eligible = rqLists.filter((l) => {
      if (l.is_default) return false;
      const isOwner = l.user_id ? l.user_id === user.id : true;
      const isCollab = !!l.collaborator_role;
      return isOwner || isCollab;
    });

    const eligibleIds = new Set(eligible.map((l) => l.id));

    // Unsubscribe removed
    for (const [listId, ch] of subscriptionsRef.current) {
      if (!eligibleIds.has(listId)) {
        ch.unsubscribe();
        subscriptionsRef.current.delete(listId);
      }
    }

    // Subscribe new
    for (const l of eligible) {
      if (subscriptionsRef.current.has(l.id)) continue;

      const channel = supabase
        .channel(`watchlist_events:${l.id}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'watchlist_events',
            filter: `watchlist_id=eq.${l.id}`,
          },
          (payload) => {
            const row: any = payload.new;
            if (!row) return;

            const eventType = row.type;
            const eventPayload = row.payload;

            // Phase 4C: Update React Query cache ONLY based on event type
            if (eventType === 'item_added' && eventPayload?.media_id && eventPayload?.media_type) {
              // Revision-based dedup: skip if this tab already has this revision
              const evtRevision: number | undefined = typeof row.revision === 'number' ? row.revision : undefined;
              if (typeof evtRevision === 'number') {
                const infiniteData = getAnyListItemsData(queryClient, l.id);
                const infiniteRev = infiniteData?.pages?.[0]?.watchlist?.revision;
                const detailData = queryClient.getQueryData<{ watchlist?: { revision?: number } }>(
                  queryKeys.lists.detail(l.id)
                );
                const detailRev = detailData?.watchlist?.revision;
                const currentCachedRevision = Math.max(
                  typeof infiniteRev === 'number' ? infiniteRev : -1,
                  typeof detailRev === 'number' ? detailRev : -1
                );
                if (currentCachedRevision >= evtRevision) {
                  return; // Already incorporated — skip to avoid duplicate
                }
              }

              // Completeness check: require item_id, media_title, AND added_at
              // Never synthesize timestamps — causes ordering drift vs server canonical order
              if (!eventPayload.item_id || !eventPayload.media_title || !eventPayload.added_at) {
                queryClient.invalidateQueries({ queryKey: queryKeys.lists.detail(l.id) });
                invalidateAllListItemsQueries(queryClient, l.id);
                return;
              }

              const newItem: WatchlistItemRow = {
                id: eventPayload.item_id,
                watchlist_id: l.id,
                media_id: eventPayload.media_id,
                media_type: eventPayload.media_type,
                media_title: eventPayload.media_title,
                media_poster: eventPayload.media_poster ?? null,
                media_year: eventPayload.media_year ?? null,
                notes: eventPayload.notes ?? null,
                position: eventPayload.position ?? null,
                rank: typeof eventPayload.rank === 'string' ? eventPayload.rank : null,
                added_at: eventPayload.added_at,
              };
              rqAddItem(l.id, newItem);
            } else if (eventType === 'item_removed' && eventPayload?.media_id && eventPayload?.media_type) {
              // Remove item from React Query cache
              rqRemoveItem(l.id, eventPayload.media_id, eventPayload.media_type);
            } else if (eventType === 'item_moved' && typeof eventPayload?.item_id === 'string') {
              // Apply move directly to React Query caches for instant cross-tab ordering
              const itemId: string = eventPayload.item_id;
              const insertBeforeId: string | null = eventPayload.insert_before_id ?? null;
              const newRank: string | undefined = typeof eventPayload.new_rank === 'string' ? eventPayload.new_rank : undefined;
              const evtRevision: number | undefined = typeof row.revision === 'number' ? row.revision : undefined;

              // Dedup: skip if this tab already has this revision (prevents echo jitter)
              if (typeof evtRevision === 'number') {
                // Read current cached revision from infinite cache or detail cache
                const infiniteData = getAnyListItemsData(queryClient, l.id);
                const infiniteRev = infiniteData?.pages?.[0]?.watchlist?.revision;
                const detailData = queryClient.getQueryData<{ watchlist?: { revision?: number } }>(
                  queryKeys.lists.detail(l.id)
                );
                const detailRev = detailData?.watchlist?.revision;
                const currentCachedRevision = Math.max(
                  typeof infiniteRev === 'number' ? infiniteRev : -1,
                  typeof detailRev === 'number' ? detailRev : -1
                );

                if (currentCachedRevision >= evtRevision) {
                  // Already incorporated — skip to avoid jitter
                  return;
                }
              }

              /**
               * Apply a move operation to a flat items array.
               * Returns the reordered array, or null if the move can't be applied
               * (missing moved item or missing insert target).
               */
              const applyMoveToItemsArray = (
                items: WatchlistItemRow[],
                movedItemId: string,
                beforeId: string | null,
                rank: string | undefined
              ): WatchlistItemRow[] | null => {
                const movedIdx = items.findIndex((it) => it.id === movedItemId);
                if (movedIdx === -1) return null; // Can't apply: moved item not in cache

                // Remove moved item
                const result = [...items];
                const [movedItem] = result.splice(movedIdx, 1);

                // Update rank if provided
                const updatedItem = rank ? { ...movedItem, rank } : movedItem;

                // Compute insertion index
                if (beforeId === null) {
                  // Move to end
                  result.push(updatedItem);
                } else {
                  const beforeIdx = result.findIndex((it) => it.id === beforeId);
                  if (beforeIdx === -1) return null; // Can't apply: target not in cache
                  result.splice(beforeIdx, 0, updatedItem);
                }

                return result;
              };

              // Track whether apply succeeded (if not, fall back to invalidation)
              let applied = false;

              // Apply to all infinite items cache variants
              setAllListItemsData(queryClient, l.id, (old) => {
                if (!old || old.pages.length === 0) return old;

                // Flatten, apply move, re-split
                const flat = old.pages.flatMap((page) => page.items);
                const moved = applyMoveToItemsArray(flat, itemId, insertBeforeId, newRank);
                if (!moved) return old; // Can't apply — unchanged, will invalidate below

                applied = true;

                // Re-split into original page sizes (infinite-scroll stability)
                let offset = 0;
                const newPages = old.pages.map((page) => {
                  const pageSize = page.items.length;
                  const slice = moved.slice(offset, offset + pageSize);
                  offset += pageSize;
                  // Update watchlist revision if available
                  const updatedWatchlist = evtRevision !== undefined && page.watchlist
                    ? { ...page.watchlist, revision: evtRevision }
                    : page.watchlist;
                  return { ...page, items: slice, watchlist: updatedWatchlist };
                });

                return { ...old, pages: newPages };
              });

              // Also patch the detail cache (used by non-watchlist-page UIs)
              if (applied) {
                const detailKey = queryKeys.lists.detail(l.id);
                const existingDetail = queryClient.getQueryData<{
                  watchlist: WatchlistMeta;
                  items: WatchlistItemRow[];
                  pagination: { page: number; limit: number; total: number | null; has_more: boolean };
                }>(detailKey);

                if (existingDetail?.items) {
                  const movedItems = applyMoveToItemsArray(existingDetail.items, itemId, insertBeforeId, newRank);
                  if (movedItems) {
                    queryClient.setQueryData(detailKey, {
                      ...existingDetail,
                      items: movedItems,
                      watchlist: evtRevision !== undefined
                        ? { ...existingDetail.watchlist, revision: evtRevision }
                        : existingDetail.watchlist,
                    });
                  } else {
                    // Detail cache has different items than infinite cache — invalidate
                    queryClient.invalidateQueries({ queryKey: detailKey });
                  }
                }
                // Detail cache doesn't exist — do nothing (no forced fetch)
              }

              if (!applied) {
                // Move couldn't be applied (items not in cache) — fall back to refetch
                queryClient.invalidateQueries({ queryKey: queryKeys.lists.detail(l.id) });
                invalidateAllListItemsQueries(queryClient, l.id);
              }
            } else {
              // For all other events (note_updated, reordered, metadata_updated, or gaps):
              // Invalidate both detail and items queries (all sort variants)
              queryClient.invalidateQueries({ queryKey: queryKeys.lists.detail(l.id) });
              invalidateAllListItemsQueries(queryClient, l.id);
            }
          }
        )
        .subscribe();

      subscriptionsRef.current.set(l.id, channel);
    }
  }, [session?.access_token, user?.id, rqLists, queryClient, rqAddItem, rqRemoveItem]);

  const value: ListsContextValue = useMemo(() => {
    // Phase 4C: React Query is the ONLY source of truth for all list data
    // No more ListsStore for reads - all functions proxy to React Query
    return {
      isHydrated: rqIsHydrated,
      lists: rqLists,
      myLists: rqMyLists,
      collabLists: rqCollabLists,
      followedLists: [],
      getListMeta,
      getListItemsStatus,
      getListItemsFetchedAtMs,
      getListItems,
      getListHasMore,
      refresh,
      fetchListItems,
      ensureList,
      loadMoreList,
      optimisticReorderByPosition,
      seedFromWatchlistDetail,
      isInList,
      isItemPending,
      toggleListItem,
      setListItemDesired,
      createWatchlist,
    };
  }, [
    rqIsHydrated,
    rqLists,
    rqMyLists,
    rqCollabLists,
    getListMeta,
    getListItemsStatus,
    getListItemsFetchedAtMs,
    getListItems,
    getListHasMore,
    refresh,
    fetchListItems,
    ensureList,
    loadMoreList,
    optimisticReorderByPosition,
    seedFromWatchlistDetail,
    isInList,
    isItemPending,
    toggleListItem,
    setListItemDesired,
    createWatchlist,
  ]);

  return <ListsContext.Provider value={value}>{children}</ListsContext.Provider>;
}

export function useLists() {
  const ctx = useContext(ListsContext);
  if (!ctx) throw new Error('useLists must be used within a ListsProvider');
  return ctx;
}


